import React, { useEffect, useState, useRef } from "react";
import { ResultRating } from "@/components/ResultRating";
import { TikTokPostOptions } from "@/components/social/TikTokPostOptions";
import { useNavigate } from "react-router-dom";
import {
  CalendarClock, CalendarDays, Plus, Loader2, Image as ImageIcon, Video, Trash2, Clock, Sparkles,
  Download, Pencil, RefreshCw, CheckCircle2, ArrowLeft, Upload, Lock, Save,
  Heart, MessageCircle, Repeat2, AlertTriangle, Copy, Eye, Zap, Link2, Send, Music2, PackagePlus } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { downloadFile, mediaUrl } from "@/lib/download";
import { copyText as clipCopy } from "@/lib/clipboard";
import { useProjects } from "@/context/ProjectContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import ImageEditor from "@/components/ImageEditor";
import JourneyBar from "@/components/JourneyBar";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import NoProject from "@/components/NoProject";
import { toast } from "sonner";
import { campaignTags } from "@/lib/hashtags";
import { GenProgress } from "@/components/GenProgress";
import { AdBriefFields, EMPTY_BRIEF, briefMissing } from "@/components/AdBriefFields";
import { Switch } from "@/components/ui/switch";
import { CharacterPicker } from "@/components/CharacterPicker";
import { DeleteX } from "@/components/DeleteX";
import { OverlayEditor } from "@/components/OverlayEditor";
import { SuperRawajPanel, useSuperRawaj, publishModeOf } from "@/components/social/SuperRawajPanel";
import { SuperRawajIntro } from "@/components/social/SuperRawajIntro";
import { ModeChoice } from "@/components/social/ModeChoice";
import { PostScript } from "@/components/PostScript";
import { GoalsAlignedNote } from "@/components/goals/GoalsAlignedNote";
import { CampaignPicks } from "@/components/goals/CampaignPicks";
import { CampaignAssets } from "@/components/CampaignAssets";
import { CampaignSuggestions } from "@/components/goals/CampaignSuggestions";
import { ProductCatalog, ProductPicker } from "@/components/ProductCatalog";
import { AddCreditsButton } from "@/components/AddCreditsButton";
import { ExtraImagesPicker } from "@/components/ExtraImagesPicker";
import { useLang } from "@/context/LangContext";

// وسم «يخدم» بلا أرقام أو وعود (5–6 كلمات) — المنشورات القديمة تُشتق من نص النتيجة الرئيسية
const servesLabel = (post) => {
  if (post.serves_label) return post.serves_label;
  if (!post.serves) return "";
  const words = post.serves.replace(/[\d٠-٩%٪]+/g, " ").replace(/\b(تحقيق|الوصول إلى|الوصول|بلوغ|خلال|بنهاية|نهاية|الدورة|الشهر|الأسبوع|في)\b/g, " ").replace(/[—-]/g, " ").split(/\s+/).filter(Boolean);
  return words.length ? `يخدم ${words.slice(0, 5).join(" ")}` : "";
};

const PLATFORMS = ["Instagram", "TikTok", "Snapchat"];

const postMeasured = (m) => !!m && (m.submitted === true || !!((m.likes || 0) + (m.comments || 0) + (m.reposts || 0)));
// يدوي: توليد + رفع + أداء · سوبر رواج: توليد + اعتماد ثم نشرٌ تلقائي (يكتمل المنشور عند نشره فعلاً)
const postComplete = (p, auto = false) => !!p.content_id && (auto ? !!p.uploaded : (!!p.uploaded && postMeasured(p.metrics)));

export default function CampaignScheduler() {
  const { activeProject } = useProjects();
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", platform: "Instagram", start_date: new Date().toISOString().slice(0, 10) });
  const [goalFocus, setGoalFocus] = useState("");
  const [products, setProducts] = useState([]);
  const [productIds, setProductIds] = useState([]);
  const [newAssets, setNewAssets] = useState({ product_image: "", logo: "", extras: [] });
  const readImg = (file, cb) => { if (!file) return; if (file.size > 4 * 1024 * 1024) return toast.error("حجم الصورة يتجاوز 4MB"); const r = new FileReader(); r.onload = () => cb(r.result); r.readAsDataURL(file); };
  const [newProduct, setNewProduct] = useState(false);
  const [brief, setBrief] = useState(EMPTY_BRIEF);
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(null);
  const [highlight, setHighlight] = useState(false);
  const { addon, reload: reloadAddon } = useSuperRawaj();
  const isAuto = (c) => publishModeOf(c, addon) === "super_rawaj";
  const superMode = !!addon?.active && addon.publish_mode === "super_rawaj";
  const [linked, setLinked] = useState(null);
  useEffect(() => {
    if (!superMode || !activeProject) { setLinked(null); return; }
    api.get(`/social/accounts?project_id=${activeProject.id}`).then((r) => setLinked(r.data.some((a) => a.status === "connected"))).catch(() => setLinked(null));
  }, [superMode, activeProject?.id]); // eslint-disable-line
  const needLink = superMode && linked === false; // سوبر رواج: الربط أولاً ثم إنشاء الحملة
  const [queue, setQueue] = useState([]); // طابور النشر التلقائي للحملة المختارة — يُظهر حالة كل منشور
  const approvedCount = (selected?.posts || []).filter((p) => p.approved).length;
  useEffect(() => {
    if (!selected || !addon?.active) { setQueue([]); return; }
    api.get(`/social/queue?campaign_id=${selected.id}`).then((r) => setQueue(r.data)).catch(() => setQueue([]));
  }, [selected?.id, selected?.auto_scheduled, approvedCount, addon?.active]); // eslint-disable-line

  const load = () => {
    if (!activeProject) { setLoading(false); return; }
    setLoading(true);
    api.get(`/campaigns?project_id=${activeProject.id}`).then((r) => { setCampaigns(r.data); if (r.data.length) setSelected((prev) => r.data.find((c) => c.id === prev?.id) || r.data[0]); }).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, [activeProject]);

  // Patch a single post inside campaigns state (keeps completion counters live).
  const patchPost = (campaignId, postId, patch) => {
    setCampaigns((prev) => prev.map((c) => c.id !== campaignId ? c : {
      ...c, posts: c.posts.map((p) => p.id !== postId ? p : { ...p, ...patch }),
    }));
    setSelected((prev) => prev && prev.id === campaignId ? {
      ...prev, posts: prev.posts.map((p) => p.id !== postId ? p : { ...p, ...patch }),
    } : prev);
  };

  const create = async () => {
    if (!form.name) return toast.error("أدخل اسم الحملة");
    if (newProduct && briefMissing(brief).length) return toast.error(`أكمل تفاصيل المنتج: ${briefMissing(brief).join("، ")}`);
    setSaving(true);
    try {
      const { data } = await api.post("/campaigns/generate-monthly", { project_id: activeProject.id, ...form, goal_focus: goalFocus, product_ids: productIds, assets: newAssets, ad_brief: newProduct ? brief : null });
      toast.success("تم إنشاء الحملة — جارٍ تجهيز النصوص والهاشتاقات تلقائياً…");
      setCampaigns([data, ...campaigns]);
      setSelected(data);
      setOpen(false);
      setForm({ name: "", platform: "Instagram", start_date: new Date().toISOString().slice(0, 10) });
      setGoalFocus(""); setProductIds([]); setNewAssets({ product_image: "", logo: "", extras: [] });
      setNewProduct(false); setBrief(EMPTY_BRIEF);
      if (data.captions_status === "generating") pollCaptions(data.id);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const refillCaptions = async () => {
    if (!selected) return;
    try {
      await api.post(`/campaigns/${selected.id}/captions`);
      setCampaigns((prev) => prev.map((p) => (p.id === selected.id ? { ...p, captions_status: "generating" } : p)));
      setSelected((prev) => (prev ? { ...prev, captions_status: "generating" } : prev));
      toast.info("بدأ تجهيز النصوص والهاشتاقات لكل المنشورات…");
      pollCaptions(selected.id);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const pollCaptions = (campaignId) => {
    let tries = 0;
    const iv = setInterval(async () => {
      tries += 1;
      try {
        const r = await api.get(`/campaigns?project_id=${activeProject.id}`);
        const c = r.data.find((x) => x.id === campaignId);
        if (c && c.captions_status && c.captions_status !== "generating") {
          clearInterval(iv);
          setCampaigns((prev) => prev.map((p) => (p.id === campaignId ? c : p)));
          setSelected((prev) => (prev && prev.id === campaignId ? c : prev));
          if (c.captions_status === "ready") toast.success("تم تجهيز نصوص وهاشتاقات كل المنشورات ✅");
          else toast.error("تعذّر تجهيز بعض النصوص تلقائياً — استخدم «إعادة صياغة النص» عند الحاجة");
        }
      } catch (e) {}
      if (tries >= 100) clearInterval(iv);
    }, 3000);
  };

  const remove = async (id) => {
    try { await api.delete(`/campaigns/${id}`); const rest = campaigns.filter((c) => c.id !== id); setCampaigns(rest); setSelected(rest[0] || null); toast.success("تم حذف الحملة"); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  if (!activeProject) return <NoProject message="اختر مشروعاً لتنفيذ حملاته: توليد، رفع، وإدخال الأداء." />;

  if (activeProject.standalone) {
    return (
      <div className="glass rounded-2xl p-12 text-center border border-border max-w-xl mx-auto" data-testid="strategy-only-guard">
        <Lock className="w-10 h-10 text-primary mx-auto mb-4" />
        <h3 className="text-xl font-bold mb-2">هذا القسم لمشاريع الاستراتيجية</h3>
        <p className="text-muted-foreground mb-6">«{activeProject.name}» مشروع محتوى. استخدم استوديو المحتوى للتوليد، أو أنشئ مشروع استراتيجية لتنفيذ الحملات.</p>
        <Button onClick={() => navigate("/app/studio")} className="bg-primary hover:bg-primary/90 text-white gap-2" data-testid="guard-go-studio"><Sparkles className="w-4 h-4" /> الانتقال لاستوديو المحتوى</Button>
      </div>
    );
  }

  const allPosts = campaigns.flatMap((c) => (c.posts || []).map((p) => ({ ...p, _auto: isAuto(c) })));
  const completedCount = allPosts.filter((p) => postComplete(p, p._auto)).length;
  const totalPosts = allPosts.length;
  const allComplete = totalPosts > 0 && completedCount === totalPosts;
  const anyAuto = campaigns.some(isAuto);

  const handleGoMeasurement = () => {
    if (allComplete) { setHighlight(false); navigate("/app/measurement"); return; }
    const firstIncomplete = campaigns.find((c) => (c.posts || []).some((p) => !postComplete(p, isAuto(c))));
    if (firstIncomplete) setSelected(firstIncomplete);
    setHighlight(true);
    toast.error(anyAuto ? "هناك عناصر ناقصة — ولّد واعتمد ما هو معلّم بالأحمر، وتُفتح التوصيات بعد نشر سوبر رواج لكل المنشورات في مواعيدها." : "هناك عناصر ناقصة — أكمِل ما هو معلّم بالأحمر في كل منشور (توليد / رفع / أداء).");
  };

  return (
    <div className="space-y-6" data-testid="campaigns-page">
      <JourneyBar current="execution" />
      {!activeProject.standalone && <GoalsAlignedNote projectId={activeProject.id} />}
      {!needLink && <div className="flex justify-end -mb-2"><AddCreditsButton /></div>}
      {!needLink && <ProductCatalog projectId={activeProject.id} onChange={setProducts} />}
      {!activeProject.standalone && !needLink && (
        <CampaignSuggestions projectId={activeProject.id} existingNames={campaigns.map((c) => c.name)}
          onPick={(c) => { setForm({ ...form, name: c.name, platform: PLATFORMS.includes(c.platform) ? c.platform : form.platform }); setGoalFocus([c.idea, c.serves ? `تخدم: ${c.serves}` : ""].filter(Boolean).join(" — ")); setOpen(true); }}
          onCustom={() => { setForm({ ...form, name: "" }); setGoalFocus(""); setOpen(true); }} />
      )}
      <SuperRawajIntro />
      {!!addon?.active && <ModeChoice addon={addon} linked={linked} onChosen={(m) => { reloadAddon(); if (m === "super_rawaj" && linked === false) navigate("/app/social"); }} />}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-extrabold font-heading">التنفيذ</h1>
          <p className="text-muted-foreground">{superMode ? "اربط حسابك، أنشئ حملتك، ولّد المحتوى واعتمده — وسوبر رواج ينشر ويسحب الأداء" : "ولّد المحتوى، ارفعه، ثم أدخِل أرقام أدائه لكل منشور"} — «{activeProject.name}»</p>
        </div>
        {needLink ? (
          <Button onClick={() => navigate("/app/social")} className="bg-rose-600 hover:bg-rose-700 text-white gap-2" data-testid="link-first-btn"><Link2 className="w-4 h-4" /> اربط حسابك أولاً ثم أنشئ الحملة</Button>
        ) : (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-primary hover:bg-primary/90 text-white gap-2" data-testid="open-create-campaign"><Plus className="w-4 h-4" /> حملة شهرية جديدة</Button>
          </DialogTrigger>
          <DialogContent className="bg-popover border-border max-h-[90vh] overflow-y-auto" dir="rtl">
            <DialogHeader><DialogTitle>إنشاء حملة شهرية</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div className="bg-primary/10 border border-primary/20 rounded-xl p-4 flex items-center gap-3">
                <Sparkles className="w-5 h-5 text-primary" />
                <p className="text-sm">سيتم جدولة <b>14 صورة + 2 فيديو</b> تلقائياً على مدار الشهر بأفضل أوقات النشر.</p>
              </div>
              {!activeProject.standalone && (
                <CampaignPicks projectId={activeProject.id} selectedName={form.name}
                  onPick={(c) => { setForm({ ...form, name: c.name, platform: PLATFORMS.includes(c.platform) ? c.platform : form.platform }); setGoalFocus([c.idea, c.serves ? `تخدم: ${c.serves}` : ""].filter(Boolean).join(" — ")); }} />
              )}
              <div><Label className="mb-2 block">اسم الحملة</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="مثال: حملة أكتوبر" className="bg-secondary border-border" data-testid="campaign-name-input" /></div>
              <ProductPicker products={products} selected={productIds} onChange={setProductIds} />
              <div className="rounded-xl border border-border bg-secondary/30 p-3 space-y-3" data-testid="new-campaign-assets">
                <p className="text-sm font-bold">مدخلات الحملة <span className="text-xs text-muted-foreground font-normal">(تُحفظ أولًا ثم تُبنى عليها نصوص الحملة وهاشتاقاتها وصورها)</span></p>
                <div className="grid grid-cols-2 gap-3">
                  {[["product_image", "صورة المنتج"], ["logo", "صورة اسم العلامة / الشعار"]].map(([k, lbl]) => (
                    <label key={k} className="rounded-lg border-2 border-dashed border-border p-2 text-center text-xs cursor-pointer flex flex-col items-center gap-1 min-h-[72px] justify-center">
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => readImg(e.target.files?.[0], (img) => setNewAssets({ ...newAssets, [k]: img }))} data-testid={`new-campaign-${k}-input`} />
                      {newAssets[k] ? <img src={newAssets[k]} alt="" className="h-12 object-contain" /> : <span>{lbl}</span>}
                    </label>
                  ))}
                </div>
                <ExtraImagesPicker value={newAssets.extras} onChange={(v) => setNewAssets({ ...newAssets, extras: v })} label="صور إضافية تخدم الحملة" hint="" testId="new-campaign-extras" />
              </div>
              <div><Label className="mb-2 block">فكرة الحملة وهدفها <span className="text-xs text-muted-foreground">(تُبنى عليها النصوص والهاشتاقات)</span></Label><Textarea rows={2} value={goalFocus} onChange={(e) => setGoalFocus(e.target.value)} placeholder="مثال: تعريف الشباب بالمنصة وجذب أول ٥٠ تسجيل عبر قصص مستقلين حقيقيين" className="bg-secondary border-border" data-testid="campaign-goal-focus-input" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="mb-2 block">المنصة</Label>
                  <Select value={form.platform} onValueChange={(v) => setForm({ ...form, platform: v })}>
                    <SelectTrigger className="bg-secondary border-border" data-testid="campaign-platform-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-popover border-border">{PLATFORMS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="mb-2 block">تاريخ البدء</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} dir="ltr" className="bg-secondary border-border" data-testid="campaign-date-input" /></div>
              </div>
              <label className="flex items-center justify-between gap-3 rounded-xl border border-border p-3 cursor-pointer">
                <span className="text-sm">الحملة لمنتج جديد؟ <span className="text-xs text-muted-foreground block">نسألك عن المنتج ليبني المخرج كل الصور والفيديوهات عليه</span></span>
                <Switch checked={newProduct} onCheckedChange={setNewProduct} data-testid="campaign-new-product" />
              </label>
              {newProduct && <AdBriefFields value={brief} onChange={setBrief} testId="campaign-brief" />}
            </div>
            <DialogFooter>
              <Button onClick={create} disabled={saving} className="w-full bg-primary hover:bg-primary/90 text-white gap-2" data-testid="submit-create-campaign">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarClock className="w-4 h-4" />} إنشاء الحملة وجدولتها
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        )}
      </div>
      {needLink && (
        <div className="rounded-xl border border-rose-500/50 bg-rose-500/5 p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="link-first-banner">
          <div className="text-sm flex items-start gap-2"><Link2 className="w-4 h-4 text-rose-400 mt-0.5 shrink-0" /><span><b>سوبر رواج مفعّل:</b> اربط حساب «{activeProject.name}» على إنستغرام أولاً، ثم أنشئ الحملة — ينشر سوبر رواج ويسحب الأداء تلقائياً. (تغيير طريقة النشر من صفحة قنوات التواصل)</span></div>
          <Button size="sm" onClick={() => navigate("/app/social")} className="bg-rose-600 hover:bg-rose-700 text-white gap-1" data-testid="link-first-banner-btn"><Link2 className="w-3.5 h-3.5" /> اربط الحساب</Button>
        </div>
      )}

      {loading ? <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
      : campaigns.length === 0 ? (
        <div className="glass rounded-2xl p-16 text-center border border-border"><CalendarClock className="w-12 h-12 text-muted-foreground mx-auto mb-4" /><p className="text-muted-foreground">لا توجد حملات بعد. أنشئ حملتك الشهرية الأولى.</p></div>
      ) : (
        <>
          <div className="grid lg:grid-cols-4 gap-6 min-w-0">
            <div className="lg:col-span-1 space-y-2 min-w-0">
              {campaigns.map((c) => {
                const done = (c.posts || []).filter((p) => postComplete(p, isAuto(c))).length;
                return (
                  <button key={c.id} onClick={() => setSelected(c)} data-testid={`campaign-item-${c.id}`}
                    className={`w-full text-start glass rounded-xl p-4 border transition-all ${selected?.id === c.id ? "border-primary bg-primary/10" : "border-border"}`}>
                    <div className="font-bold text-sm">{c.name}</div>
                    <div className="text-xs text-muted-foreground mt-1">{c.platform} · {done}/{c.posts.length} مكتمل</div>
                  </button>
                );
              })}
            </div>
            {selected && (
              <div className="lg:col-span-3 glass rounded-2xl p-6 border border-border min-w-0 overflow-hidden" data-testid="campaign-detail">
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <h2 className="text-xl font-bold">{selected.name}</h2>
                    <p className="text-sm text-muted-foreground">{selected.platform} · يبدأ {selected.start_date}</p>
                  </div>
                  <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-destructive gap-1" onClick={() => remove(selected.id)} data-testid={`delete-campaign-${selected.id}`}><Trash2 className="w-4 h-4" /> حذف</Button>
                </div>
                {selected.goal_focus && (
                  <p className="mb-3 text-xs text-muted-foreground bg-primary/5 border border-primary/20 rounded-lg px-3 py-2" data-testid="campaign-goal-focus"><b className="text-primary">هدف الحملة:</b> {selected.goal_focus}</p>
                )}
                <div className="mb-4">
                  <CampaignAssets campaign={selected} onSaved={(assets) => { setSelected((p) => ({ ...p, assets })); setCampaigns((prev) => prev.map((c) => (c.id === selected.id ? { ...c, assets } : c))); }} />
                </div>
                {selected.captions_status === "generating" && (
                  <div className="mb-4 flex items-center gap-2 text-xs text-accent bg-accent/10 border border-accent/30 rounded-lg px-3 py-2" data-testid="captions-generating">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> جارٍ تجهيز نصوص وهاشتاقات كل المنشورات تلقائياً… قد يستغرق دقيقة أو دقيقتين
                  </div>
                )}
                {selected.captions_status !== "generating" && selected.posts.some((p) => !(p.caption || "").trim() || !(p.hashtags || []).length) && (
                  <div className="mb-4 flex items-center justify-between gap-3 flex-wrap text-xs bg-rose-500/5 border border-rose-500/40 rounded-lg px-3 py-2" data-testid="captions-failed">
                    <span className="flex items-center gap-1.5 text-rose-400 font-bold"><AlertTriangle className="w-3.5 h-3.5" /> بعض المنشورات بلا نص أو هاشتاقات — لا يُفتح توليد الصور والفيديو قبلها</span>
                    <Button size="sm" onClick={refillCaptions} className="bg-rose-600 hover:bg-rose-700 text-white gap-1 h-7 text-xs" data-testid="captions-refill-btn"><RefreshCw className="w-3.5 h-3.5" /> ولّد النصوص والهاشتاقات لكل المنشورات</Button>
                  </div>
                )}
                <SuperRawajPanel campaign={selected} queue={queue} onScheduled={load} onQueue={setQueue} />
                <div className="space-y-3" data-testid="campaign-calendar-grid">
                  {selected.posts.map((p, i) => (
                    <PostCard key={p.id || i} campaignId={selected.id} platform={selected.platform} projectId={selected.project_id || activeProject?.id} post={p} campaignProduct={`${selected.ad_brief?.product_name || ""} ${selected.ad_brief?.product_type || ""} ${selected.name || ""}`.trim()} productImage={selected.assets?.product_image || activeProject?.product_image || ""} onEdit={setEditing} onPatch={patchPost} highlight={highlight} auto={isAuto(selected)} queueItem={queue.find((q) => q.post_id === p.id)} captionsGenerating={selected.captions_status === "generating"} />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Completion + gated transition to recommendations */}
          <div className="glass rounded-2xl p-5 border border-primary/30 bg-primary/5" data-testid="execution-progress">
            <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
              <h3 className="font-bold flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-primary" /> اكتمال التنفيذ
              </h3>
              <span className="text-sm font-bold" data-testid="execution-count">{completedCount} / {totalPosts} منشور مكتمل</span>
            </div>
            <div className="h-2.5 rounded-full bg-secondary overflow-hidden mb-4">
              <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${totalPosts ? (completedCount / totalPosts) * 100 : 0}%` }} />
            </div>
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <p className="text-xs text-muted-foreground">
                {anyAuto ? <>مع سوبر رواج: لكل منشور <b>توليد المحتوى</b> + <b>اعتماده</b>، وما يُعتمد يُجدول فوراً وينشره سوبر رواج في موعده ويسحب أداءه. تُفتح التوصيات والإغلاق عند <b>نشر كل منشورات الحملة</b>.</>
                  : <>لكل منشور: <b>توليد المحتوى</b> + <b>تم الرفع</b> + <b>إدخال الأداء</b> (إعجابات/تعليقات/إعادة نشر). عند اكتمال الكل يُفتح الانتقال للتوصيات والإغلاق.</>}
              </p>
              <Button onClick={handleGoMeasurement}
                className={`gap-2 font-bold shrink-0 text-white ${allComplete ? "bg-primary hover:bg-primary/90" : "bg-rose-600 hover:bg-rose-700"}`} data-testid="go-measurement-btn">
                {allComplete ? <ArrowLeft className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
                {allComplete ? "التوصيات والإغلاق" : "تحقّق من العناصر الناقصة"}
              </Button>
            </div>
          </div>
        </>
      )}
      <ImageEditor
        open={!!editing}
        item={editing}
        projectLogo={activeProject?.logo}
        brandName={activeProject?.name}
        suggestedTexts={editing ? [
          { label: "الموضوع", value: editing.topic },
          { label: "الدعوة", value: editing.cta },
          { label: "العلامة", value: activeProject?.name },
        ].filter((t) => t.value) : []}
        onClose={() => setEditing(null)}
      />
    </div>
  );
}

const fmtRiyadh = (iso) => new Date(iso).toLocaleString("ar-SA-u-ca-gregory-nu-latn", { timeZone: "Asia/Riyadh", dateStyle: "medium", timeStyle: "short" });
// سبب/حالة النشر التلقائي لكل منشور — حتى يعرف العميل لماذا لم يُنشر بعد
function autoNote(approved, q, platform, t) {
  if (!approved) return t("بعد الاعتماد ينشره سوبر رواج في موعده ويسحب أرقام أدائه تلقائياً.", "After approval Super Rawaj publishes it on schedule and pulls its metrics automatically.");
  if (!q) return t("معتمد لكنه غير مُدرج في طابور النشر بعد — اضغط «جدوِل النشر التلقائي» أعلى الحملة لإدراجه، أو انشره الآن.", "Approved but not in the publish queue yet — press “Schedule auto-publish” above the campaign, or publish now.");
  if (q.status === "queued") return `${t("مجدول — ينشره سوبر رواج تلقائياً في", "Scheduled — Super Rawaj will publish it at")} ${fmtRiyadh(q.publish_at)} (${t("توقيت الرياض", "Riyadh time")})${t("، أو انشره الآن.", ", or publish now.")}`;
  if (q.status === "awaiting_account") return t(`بانتظار ربط حساب ${platform} لهذا المشروع — اربطه من «قنوات التواصل» ليُنشر في موعده.`, `Waiting for a connected ${platform} account — link it from Social Channels.`);
  if (q.status === "failed") return `${t("فشل النشر التلقائي", "Auto-publish failed")}: ${q.error || ""} — ${t("انشره الآن لإعادة المحاولة.", "publish now to retry.")}`;
  if (q.status === "manual") return q.error || t("النشر التلقائي على هذه المنصة قيد التفعيل — انشر يدوياً.", "Auto-publish on this platform is being enabled — publish manually.");
  if (q.status === "skipped") return `${t("تم تجاوز الموعد", "Slot skipped")}: ${q.error || ""}`;
  return t("معتمد — ينشره سوبر رواج في موعده، أو انشره الآن.", "Approved — Super Rawaj publishes it on schedule, or publish now.");
}

function PostCard({ campaignId, platform, projectId, post, onEdit, onPatch, highlight, auto, queueItem, captionsGenerating, campaignProduct = "", productImage = "" }) {

  const [approved, setApproved] = useState(!!post.approved);
  const [approving, setApproving] = useState(false);
  const { t } = useLang();
  const [publishing, setPublishing] = useState(false);
  const [permalink, setPermalink] = useState(post.permalink || "");
  const isTikTok = platform === "TikTok";
  const [ttOpen, setTtOpen] = useState(false);
  const [ttOpts, setTtOpts] = useState(post.tiktok || null);
  const ttReady = !isTikTok || !!ttOpts?.consent;
  // نشر فوري على الحساب المرتبط (سوبر رواج) — بدل انتظار الموعد المجدول
  const publishNow = async () => {
    if (isTikTok && !ttReady) { setTtOpen(true); return; }
    if (!window.confirm(t("نشر هذا المنشور الآن على حسابك المرتبط؟", "Publish this post now to your connected account?"))) return;
    setPublishing(true);
    try {
      const { data } = await api.post(`/social/publish-now/${campaignId}/${post.id}`);
      setUploaded(true); setPermalink(data.permalink || ""); onPatch(campaignId, post.id, { uploaded: true, auto_published: true, permalink: data.permalink });
      toast.success(isTikTok ? t("أُرسل الفيديو إلى تيك توك ✅ — قد يستغرق ظهوره دقائق", "Sent to TikTok ✅ — it may take a few minutes to appear") : t("تم النشر على إنستغرام ✅", "Published to Instagram ✅"));
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setPublishing(false); }
  };
  // اعتماد العميل للمحتوى بعد مراجعته — شرط الجدولة التلقائية
  const toggleApprove = async () => {
    if (isTikTok && auto && !approved && !ttReady) { setTtOpen(true); return; }
    setApproving(true);
    try {
      const next = !approved;
      const { data } = await api.put(`/campaigns/${campaignId}/posts/${post.id}/approved`, { approved: next });
      setApproved(next); onPatch(campaignId, post.id, { approved: next });
      toast.success(next ? (data.queued ? "تم اعتماد المحتوى وجدولته للنشر التلقائي ✅" : "تم اعتماد المحتوى") : "أُلغي الاعتماد");
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setApproving(false); }
  };
  const [caption, setCaption] = useState(post.caption || "");
  const [hashtags, setHashtags] = useState(campaignTags(post.hashtags));
  const [media, setMedia] = useState(null);
  const [loading, setLoading] = useState(false);
  const [regen, setRegen] = useState(false);
  const [uploaded, setUploaded] = useState(!!post.uploaded);
  const [metrics, setMetrics] = useState({
    likes: post.metrics?.likes || "",
    comments: post.metrics?.comments || "",
    reposts: post.metrics?.reposts || "",
  });
  const [savingM, setSavingM] = useState(false);
  const [metricsSaved, setMetricsSaved] = useState(post.metrics?.submitted === true);
  const [previewOpen, setPreviewOpen] = useState(false);
  const pollRef = useRef(null);
  const [job, setJob] = useState(null);
  // تُستعاد الشخصية والصوت والأسلوب المعتمدة من معاينة الصوت المحفوظة — لا تضيع بإعادة تحميل الصفحة
  const [gender, setGender] = useState(post.voice_preview?.voice ? (post.voice_preview.gender || "male") : "male");
  const [pick, setPick] = useState(post.voice_preview?.voice ? { voice: post.voice_preview.voice, style: post.voice_preview.style || "" } : {});
  const loadingRef = useRef(false);
  useEffect(() => () => clearInterval(pollRef.current), []);
  // Keep local text in sync when captions arrive from the background generation.
  useEffect(() => { setCaption(post.caption || ""); setHashtags(campaignTags(post.hashtags)); }, [post.caption, post.hashtags]);

  // Load already-generated media when the page opens so it can be previewed.
  useEffect(() => {
    if (post.content_id && !media) {
      const ep = post.type === "video" ? "video" : "image";
      const apply = (r) => {
        if (r.data.status === "completed") {
          clearInterval(pollRef.current); setLoading(false);
          setMedia(ep === "video" ? { type: "video", url: r.data.url } : { type: "image", data: r.data.data });
          return true;
        }
        if (r.data.status === "failed") {
          clearInterval(pollRef.current);
          if (loadingRef.current) toast.error(r.data.error || "فشل التوليد");
          setLoading(false); loadingRef.current = false;
          return true;
        }
        setJob((j) => ({ start: j?.start || Date.parse(r.data.created_at) || Date.now(), progress: r.data.progress || 0, stage: r.data.stage || "scene", queue_position: r.data.queue_position }));
        return false;
      };
      api.get(`/content/${ep}/${post.content_id}/status`).then((r) => {
        if (!apply(r)) {
          // Resume an in-flight generation after reload: show progress and keep polling.
          setLoading(true); loadingRef.current = true;
          pollRef.current = setInterval(() => api.get(`/content/${ep}/${post.content_id}/status`).then(apply).catch(() => {}), 2500);
        }
      }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const generated = !!post.content_id || !!media;
  const measured = metricsSaved || post.metrics?.submitted === true;
  const complete = generated && (auto ? uploaded : (uploaded && measured));
  const isVideo = post.type === "video";
  const needGen = highlight && !generated;
  const needApprove = highlight && auto && generated && !approved;
  // قانون التوليد: الصورة/الفيديو يُبنيان على نص المنشور وهاشتاقاته — لا توليد قبلهما
  const hasText = !!caption.trim() && (post.hashtags || []).length > 0;
  const hasScript = !!post.script_approved && !!(post.script || "").trim(); // فيديو الحملة يُبنى على سكربت معتمد
  const vp = post.voice_preview || {};
  const hasVoice = !!vp.approved && vp.voice === (pick.voice || "") && (vp.style || "") === (pick.style || ""); // الخطوة ٢: صوت التوليد محفوظ بنفس الصوت والأسلوب — ما سمعه العميل هو ما يُنتج
  const needUpload = highlight && !uploaded && !auto;
  const needMetrics = highlight && !measured && !auto;

  const markUploaded = async () => {
    const next = !uploaded;
    setUploaded(next);
    try {
      await api.put(`/campaigns/${campaignId}/posts/${post.id}/uploaded`, { uploaded: next });
      onPatch(campaignId, post.id, { uploaded: next });
      toast.success(next ? "تم تعليم المنشور كمرفوع ✅" : "أُلغي تعليم الرفع");
    } catch (err) {
      setUploaded(!next);
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  const genImage = async () => {
    setLoading(true); setJob({ start: Date.now(), progress: 0, stage: "scene" });
    try {
      const { data } = await api.post(`/campaigns/${campaignId}/posts/${post.id}/image`, {});
      onPatch(campaignId, post.id, { content_id: data.content_id, status: "processing" });
      toast.info("جارٍ توليد الصورة…");
      pollRef.current = setInterval(async () => {
        try {
          const r = await api.get(`/content/image/${data.content_id}/status`);
          setJob((j) => ({ ...(j || { start: Date.now() }), progress: r.data.progress || 0, stage: r.data.stage || "scene", queue_position: r.data.queue_position }));
          if (r.data.status === "completed") {
            clearInterval(pollRef.current);
            setMedia({ type: "image", data: r.data.data });
            onPatch(campaignId, post.id, { status: "generated" });
            setLoading(false);
            toast.success("تم توليد الصورة ✅");
          } else if (r.data.status === "failed") {
            clearInterval(pollRef.current); setLoading(false);
            toast.error(r.data.error || "فشل توليد الصورة");
          }
        } catch (e) {}
      }, 2000);
    } catch (err) { setLoading(false); toast.error(formatError(err.response?.data?.detail)); }
  };

  const genVideo = async (premium = false) => {
    setLoading(true); setJob({ start: Date.now(), progress: 0, stage: "scene" });
    try {
      const { data } = await api.post(`/campaigns/${campaignId}/posts/${post.id}/video`, { premium, gender, voice: pick.voice || "", anim_gender: pick.animGender || "", voice_style: pick.style || "" });
      onPatch(campaignId, post.id, { content_id: data.content_id, status: "processing" });
      toast.info("بدأ توليد الفيديو، قد يستغرق دقائق…");
      pollRef.current = setInterval(async () => {
        try {
          const r = await api.get(`/content/video/${data.content_id}/status`);
          setJob((j) => ({ ...(j || { start: Date.now() }), progress: r.data.progress || 0, stage: r.data.stage || "scene", queue_position: r.data.queue_position }));
          if (r.data.status === "completed") { clearInterval(pollRef.current); if (r.data.voice_warning) toast.warning(r.data.voice_warning, { duration: 12000 }); setMedia({ type: "video", url: r.data.url }); setLoading(false); toast.success(r.data.voice_name ? `اكتمل الفيديو 🎬 بصوت ${r.data.voice_name}` : "اكتمل الفيديو 🎬"); }
          else if (r.data.status === "failed") { clearInterval(pollRef.current); setLoading(false); toast.error(r.data.error || "فشل الفيديو"); }
        } catch (e) {}
      }, 2000);
    } catch (err) { setLoading(false); toast.error(formatError(err.response?.data?.detail)); }
  };

  const regenCaption = async () => {
    setRegen(true);
    try {
      const { data } = await api.post(`/campaigns/${campaignId}/posts/${post.id}/caption`);
      setCaption(data.caption); setHashtags(campaignTags(data.hashtags));
      onPatch(campaignId, post.id, { caption: data.caption, hashtags: data.hashtags });
      toast.success("تم تحديث النص");
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setRegen(false); }
  };

  const setM = (field, val) => { setMetrics((prev) => ({ ...prev, [field]: val })); setMetricsSaved(false); };

  const copyContent = async () => {
    const txt = [caption, (hashtags || []).join(" ")].filter(Boolean).join("\n\n");
    if (!txt.trim()) return toast.error("لا يوجد نص بعد — اضغط «إعادة صياغة النص» أولاً");
    if (await clipCopy(txt)) toast.success("تم نسخ المحتوى والهاشتاقات ✅"); else toast.error("تعذّر النسخ، حدّد النص وانسخه يدوياً");
  };

  const downloadImage = () => {
    if (!media?.data) return;
    downloadFile(`data:image/png;base64,${media.data}`, `${(post.topic || "image").slice(0, 30)}.png`);
  };
  const downloadVideo = () => {
    if (!media?.url) return;
    downloadFile(media.url, `${(post.topic || "video").slice(0, 30)}.mp4`);
  };

  const saveMetrics = async () => {
    setSavingM(true);
    try {
      const payload = {
        likes: Number(metrics.likes) || 0,
        comments: Number(metrics.comments) || 0,
        reposts: Number(metrics.reposts) || 0,
      };
      await api.put(`/campaigns/${campaignId}/posts/${post.id}/metrics`, payload);
      onPatch(campaignId, post.id, { metrics: { ...payload, submitted: true } });
      setMetricsSaved(true);
      toast.success("تم حفظ أرقام أداء المنشور 📊");
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setSavingM(false); }
  };

  return (
    <div className={`bg-secondary rounded-xl p-4 border ${complete ? "border-emerald-500/30" : "border-transparent"}`} data-testid={`post-card-${post.id}`}>
      <div className="flex items-start gap-3">
        <div className="relative shrink-0">
        {media && post.content_id && (
          <DeleteX testId={`delete-media-${post.id}`} className="absolute -top-2 -end-2 z-10 !w-6 !h-6"
            onConfirm={async () => {
              try { await api.delete(`/content/${post.content_id}`); setMedia(null); onPatch(campaignId, post.id, { content_id: null, status: "scheduled" }); toast.success("تم حذف المحتوى"); }
              catch (err) { toast.error(formatError(err.response?.data?.detail)); }
            }} />
        )}
        {media?.type === "image" && media.data ? (
          <img src={`data:image/png;base64,${media.data}`} alt="" onClick={() => setPreviewOpen(true)} className="w-20 h-20 rounded-lg object-cover border border-border shrink-0 cursor-pointer hover:ring-2 hover:ring-primary transition" data-testid={`thumb-${post.id}`} />
        ) : media?.type === "video" && media.url ? (
          <div onClick={() => setPreviewOpen(true)} className="relative w-20 h-20 shrink-0 cursor-pointer group" data-testid={`thumb-${post.id}`}>
            <video src={mediaUrl(media.url)} className="w-20 h-20 rounded-lg object-cover border border-border" />
            <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/30 group-hover:bg-black/40 transition"><Eye className="w-6 h-6 text-white" /></div>
          </div>
        ) : (
          <div className={`w-20 h-20 rounded-lg flex items-center justify-center shrink-0 ${isVideo ? "bg-sky-500/15 text-sky-400" : "bg-accent/15 text-accent"}`}>
            {isVideo ? <Video className="w-6 h-6" /> : <ImageIcon className="w-6 h-6" />}
          </div>
        )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="font-bold text-sm">{post.topic}</span>
            {servesLabel(post) && <span className="block mt-1 text-[11px] text-accent bg-accent/10 border border-accent/20 rounded-md px-2 py-0.5 w-fit max-w-full whitespace-normal break-words leading-snug" title={post.serves} data-testid={`post-serves-${post.id}`}>{servesLabel(post)}</span>}
            {post.product_name && <span className="block mt-1 text-[11px] text-primary bg-primary/10 border border-primary/20 rounded-md px-2 py-0.5 w-fit max-w-full truncate" data-testid={`post-product-${post.id}`}>المنتج: {post.product_name}</span>}
            <span className="text-[11px] bg-primary/15 text-primary px-2 py-0.5 rounded-full flex items-center gap-1" data-testid={`post-date-${post.id}`}><CalendarDays className="w-3 h-3" /> {post.date} <span className="opacity-50">·</span> <Clock className="w-3 h-3" /> {post.time} <span className="opacity-50">·</span> {platform}</span>
            <span className="text-[11px] bg-background border border-border px-2 py-0.5 rounded-full">{isVideo ? "فيديو" : "صورة"}</span>
            {complete && <span className="text-[11px] bg-emerald-500/15 text-emerald-400 px-2 py-0.5 rounded-full flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> مكتمل</span>}
          </div>
          <p className="text-xs text-muted-foreground whitespace-pre-line leading-relaxed">{caption}</p>
          {hashtags?.length > 0 && <div className="flex flex-wrap gap-1 mt-2">{hashtags.map((h, i) => <span key={i} className="text-[10px] text-primary">{h}</span>)}</div>}
        </div>
      </div>

      {/* Step 1: generation + text — الشخصية والصوت والأسلوب أولًا، ثم السكربت والاستماع */}
      {isVideo && hasText && (
        <div className="mt-3 space-y-1" data-testid={`video-setup-${post.id}`}>
          <span className="text-xs font-bold flex items-center gap-1.5"><Video className="w-3.5 h-3.5" /> شخصية الفيديو وصوته وأسلوب الأداء</span>
          <CharacterPicker value={gender} onChange={setGender} pick={pick} onPick={setPick} testId={`character-${post.id}`} compact productName={post.product_name || campaignProduct} />
          <button type="button" onClick={() => document.querySelector('[data-testid="campaign-assets"]')?.scrollIntoView({ behavior: "smooth", block: "center" })}
            className={`w-full text-start text-[11px] rounded-lg border px-2.5 py-1.5 flex items-center gap-2 ${productImage ? "border-emerald-500/40 bg-emerald-500/5 text-emerald-700 dark:text-emerald-400" : "border-rose-500/40 bg-rose-500/5 text-rose-700 dark:text-rose-300"}`} data-testid={`post-product-${post.id}`}>
            {productImage ? <img src={productImage} alt="" className="w-7 h-7 rounded-md object-cover border border-border" /> : <PackagePlus className="w-4 h-4" />}
            {productImage ? "صورة المنتج الحقيقية ستظهر في مشاهد هذا الفيديو — اضغط لتغييرها" : "لا توجد صورة منتج — اضغط لرفعها من «صور الحملة» ليظهر منتجك نفسه في الإعلان"}
          </button>
        </div>
      )}
      {isVideo && hasText && <PostScript campaignId={campaignId} post={post} onPatch={onPatch} disabled={!hasText} voice={pick.voice || ""} style={pick.style || ""} />}
      {/* 1) الإنشاء */}
      <StepRow n="1" label={t("الإنشاء", "Create")} state={needGen ? "need" : generated ? "done" : "idle"} testId={`step-create-${post.id}`}>
        {!hasText && (
          <span className="w-full text-[11px] text-accent flex items-center gap-1" data-testid={`text-first-${post.id}`}>
            {captionsGenerating ? <Loader2 className="w-3 h-3 animate-spin shrink-0" /> : <AlertTriangle className="w-3 h-3 shrink-0" />}
            <span>{captionsGenerating ? t("جارٍ تجهيز النص والهاشتاقات — يُفتح التوليد فور اكتمالهما", "Preparing text & hashtags — generation unlocks when done") : t("ولّد النص والهاشتاقات أولاً («إعادة صياغة النص») — فالصورة والفيديو يُبنيان عليهما", "Generate the text & hashtags first (“Rewrite text”) — the image/video is built on them")}</span>
          </span>
        )}
        {isVideo && !hasText && <CharacterPicker value={gender} onChange={setGender} pick={pick} onPick={setPick} testId={`character-${post.id}`} compact productName={post.product_name || campaignProduct} />}
        {!isVideo ? (
          <Button size="sm" onClick={genImage} disabled={loading || !hasText} title={hasText ? "" : t("ولّد النص والهاشتاقات أولاً", "Generate text first")} className={`${ACT(needGen)} gap-1 h-8 text-xs`} data-testid={`gen-image-${post.id}`}>
            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} <span>{media?.data ? t("إعادة توليد الصورة", "Regenerate image") : t("ولّد الصورة", "Generate image")}</span>
          </Button>
        ) : (
          <Button size="sm" onClick={() => genVideo(false)} disabled={loading || !hasText || !hasScript || !hasVoice} title={!hasText ? t("ولّد النص والهاشتاقات أولاً", "Generate text first") : !hasScript ? t("ولّد السكربت واعتمده أولاً", "Generate & approve the script first") : !hasVoice ? t("اسمع التعليق ثم احفظ صوت التوليد أولاً", "Listen & save the voice first") : ""} className={`${ACT(needGen)} gap-1 h-8 text-xs`} data-testid={`gen-video-${post.id}`}>
            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Video className="w-3 h-3" />} <span>{media?.url ? t("إعادة توليد الفيديو", "Regenerate video") : t("ولّد الفيديو", "Generate video")}</span>
          </Button>
        )}
        <Button size="sm" variant="outline" onClick={regenCaption} disabled={regen} className="gap-1 h-8 text-xs border-border" data-testid={`regen-caption-${post.id}`}>
          {regen ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} <span>{t("إعادة صياغة النص", "Rewrite text")}</span>
        </Button>
        {isVideo && hasText && !loading && (!hasScript || !hasVoice) && (
          <p className="w-full text-[11px] text-amber-800 dark:text-amber-300" data-testid={`video-next-step-${post.id}`}>
            {!hasScript ? "الخطوة التالية: ولّد السكربت واعتمده (الصندوق أعلاه) ليتفعّل زر الفيديو" : "الخطوة التالية: اسمع التعليق ثم اضغط «احفظ صوت التوليد» في الصندوق الأصفر أعلاه ليتفعّل زر الفيديو"}
          </p>
        )}
        {loading && <div className="w-full"><GenProgress kind={isVideo ? "video" : "image"} job={job} compact /></div>}
      </StepRow>

      {/* 2) الأدوات */}
      {media && (
        <StepRow n="2" label={t("المراجعة والأدوات", "Review & tools")} state="idle" testId={`step-tools-${post.id}`}>
          <Button size="sm" variant="outline" onClick={() => setPreviewOpen(true)} className="border-primary/40 text-primary gap-1 h-8 text-xs" data-testid={`preview-${post.id}`}><Eye className="w-3 h-3" /> <span>{t("معاينة", "Preview")}</span></Button>
          {media?.type === "image" && media.data && (
            <>
              <Button size="sm" variant="outline" onClick={() => onEdit({ ...post, title: post.topic, data: media.data, overlay_text: "" })} className="border-border gap-1 h-8 text-xs" data-testid={`edit-${post.id}`}><Pencil className="w-3 h-3" /> <span>{t("تحرير", "Edit")}</span></Button>
              <Button size="sm" variant="outline" onClick={downloadImage} className="border-border gap-1 h-8 text-xs" data-testid={`download-image-${post.id}`}><Download className="w-3 h-3" /> <span>{t("تنزيل الصورة", "Download image")}</span></Button>
            </>
          )}
          {media?.type === "video" && media.url && (
            <Button size="sm" variant="outline" onClick={downloadVideo} className="border-border gap-1 h-8 text-xs"><Download className="w-3 h-3" /> <span>{t("تنزيل الفيديو", "Download video")}</span></Button>
          )}
          {post.content_id && (
            <OverlayEditor contentId={post.content_id} testId={`overlay-${post.id}`} className="h-8 text-xs"
              onUpdated={(d) => setMedia((m) => ({ ...m, ...(d.data ? { data: d.data } : {}), ...(d.url ? { url: d.url } : {}) }))} />
          )}
          {caption && (
            <Button size="sm" variant="outline" onClick={copyContent} className="gap-1 h-8 text-xs border-border text-primary" data-testid={`copy-content-${post.id}`}>
              <Copy className="w-3 h-3" /> <span>{t("نسخ المحتوى + الهاشتاقات", "Copy caption + hashtags")}</span>
            </Button>
          )}
        </StepRow>
      )}

      {/* 3) القرار */}
      {generated && (
        <StepRow n="3" label={auto ? t("الاعتماد والنشر", "Approve & publish") : t("الاعتماد", "Approve")} state={needApprove ? "need" : approved ? "done" : "idle"} testId={`step-decide-${post.id}`}>
          {isTikTok && auto && !uploaded && (
            <Button size="sm" variant="outline" onClick={() => setTtOpen(true)} className={`gap-1 h-8 text-xs ${ttReady ? "border-emerald-500/50 text-emerald-700 dark:text-emerald-300" : "border-amber-500/60 text-amber-700 dark:text-amber-300"}`} data-testid={`tiktok-settings-${post.id}`}>
              <Music2 className="w-3 h-3" /> <span>{ttReady ? t("إعدادات تيك توك ✓", "TikTok settings ✓") : t("إعدادات تيك توك (مطلوبة)", "TikTok settings (required)")}</span>
            </Button>
          )}
          {!uploaded && (
            <Button size="sm" onClick={toggleApprove} disabled={approving} className={`${auto && approved ? "bg-sky-600 hover:bg-sky-700 text-white" : ACT(!approved, approved)} gap-1 h-8 text-xs`} data-testid={`approve-${post.id}`}>
              {approving ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />} <span>{approved ? (auto ? t("معتمد — جارٍ النشر", "Approved — publishing") : t("معتمد ✓", "Approved ✓")) : t("اعتمد المحتوى", "Approve content")}</span>
            </Button>
          )}
          {auto && approved && (
            uploaded
              ? <span className="inline-flex items-center gap-1 h-8 px-3 rounded-md text-xs font-bold text-white bg-emerald-600" data-testid={`published-${post.id}`}><CheckCircle2 className="w-3 h-3" /> <span>{t("منشور ✓", "Published ✓")}</span>{permalink && <a href={permalink} target="_blank" rel="noreferrer" className="underline mx-1" data-testid={`permalink-${post.id}`}>{t("عرض", "View")}</a>}</span>
              : <Button size="sm" variant="outline" onClick={publishNow} disabled={publishing} className="gap-1 h-8 text-xs border-border" data-testid={`publish-now-${post.id}`}>
                  {publishing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} <span>{t("انشر الآن", "Publish now")}</span>
                </Button>
          )}
          {auto && approved && uploaded && platform === "tiktok" && (
            <span className="w-full text-[11px] text-muted-foreground flex items-center gap-1.5" data-testid={`tiktok-published-note-${post.id}`}><Zap className="w-3.5 h-3.5 text-primary shrink-0" />
              <span>{permalink
                ? t("نُشر بخيار «أنا فقط» (قبل اعتماد التطبيق) — تجده في تبويب القفل 🔒 بملفك على تيك توك، لا في الشبكة العامة.", "Posted as \"Only me\" (pre-approval) — find it under the 🔒 tab on your TikTok profile, not the public grid.")
                : t("تيك توك تعالج المنشور الآن (قد يستغرق دقائق). بخيار «أنا فقط» يظهر في تبويب القفل 🔒 بملفك لا في الشبكة العامة. إن رفضته تيك توك ستصلك رسالة وتعود الحالة إلى «انشر الآن».", "TikTok is processing the post (may take minutes). As \"Only me\" it appears under the 🔒 tab on your profile, not the public grid. If TikTok rejects it you'll get an email and the button returns.")}</span>
            </span>
          )}
          {auto && !uploaded && (
            <span className={`w-full text-[11px] flex items-center gap-1.5 ${["failed", "manual", "awaiting_account"].includes(queueItem?.status) || (approved && !queueItem) ? "text-amber-600 dark:text-amber-400 font-bold" : "text-muted-foreground"}`} data-testid={`auto-note-${post.id}`}><Zap className="w-3.5 h-3.5 text-primary shrink-0" /> <span>{autoNote(approved, queueItem, platform, t)}</span></span>
          )}
          {auto && uploaded && post.metrics?.synced_at && (
            <span className="w-full text-[11px] text-muted-foreground" data-testid={`auto-metrics-${post.id}`}>{t("الأداء", "Performance")}: {post.metrics.likes || 0} {t("إعجاب", "likes")} · {post.metrics.comments || 0} {t("تعليق", "comments")} · {post.metrics.views || 0} {t("مشاهدة", "views")}</span>
          )}
        </StepRow>
      )}

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-lg bg-card border-border max-h-[90vh] overflow-y-auto" data-testid={`preview-dialog-${post.id}`}>
          <DialogHeader><DialogTitle className="text-start">معاينة قبل الحفظ — {post.topic}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {media?.type === "image" && media.data && (
              <img src={`data:image/png;base64,${media.data}`} alt="" className="rounded-xl w-full border border-border" data-testid={`preview-media-${post.id}`} />
            )}
            {media?.type === "video" && media.url && (
              <video src={mediaUrl(media.url)} controls autoPlay playsInline className="rounded-xl w-full border border-border max-h-[55vh] bg-black" data-testid={`preview-media-${post.id}`} />
            )}
            <div className="space-y-2">
              <Label className="text-start block">نص المنشور + الهاشتاقات (انسخه وأرفقه عند الرفع)</Label>
              <Textarea readOnly value={[caption, (hashtags || []).join(" ")].filter(Boolean).join("\n\n") || "لا يوجد نص بعد — اضغط «إعادة صياغة النص»"} rows={6} onFocus={(e) => e.target.select()} className="bg-secondary border-border leading-relaxed text-start select-text" data-testid={`preview-text-${post.id}`} />
            </div>
          </div>
          <DialogFooter className="flex-row gap-2 sm:justify-start">
            <Button onClick={copyContent} className="gap-2 bg-primary hover:bg-primary/90 text-white flex-1" data-testid={`preview-copy-${post.id}`}><Copy className="w-4 h-4" /> نسخ النص + الهاشتاقات</Button>
            {media?.type === "image" && media.data && (
              <Button variant="outline" onClick={downloadImage} className="gap-2 border-border flex-1" data-testid={`preview-download-${post.id}`}><Download className="w-4 h-4" /> تنزيل الصورة</Button>
            )}
            {media?.type === "video" && media.url && (
              <Button variant="outline" onClick={downloadVideo} className="gap-2 border-border w-full flex-1" data-testid={`preview-download-${post.id}`}><Download className="w-4 h-4" /> تنزيل الفيديو</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 4) الرفع اليدوي */}
      {!auto && generated && (
        <StepRow n="4" label={t("الرفع", "Upload")} state={needUpload ? "need" : uploaded ? "done" : "idle"} testId={`upload-row-${post.id}`}>
          <span className="w-full text-[11px] flex items-start gap-1.5">
            <Upload className="w-3.5 h-3.5 text-accent shrink-0 mt-0.5" />
            <span>{uploaded ? t("تم الرفع — أدخِل أرقام الأداء بعد النشر.", "Uploaded — enter the performance numbers after publishing.")
              : t(`انسخ النص والهاشتاقات أعلاه وأرفقهما مع المحتوى، ثم ارفعه على ${platform} بتاريخ ${post.date} الساعة ${post.time} واضغط «تم الرفع».`, `Copy the caption and hashtags above, attach them to the content, upload it to ${platform} on ${post.date} at ${post.time}, then press “Uploaded”.`)}</span>
          </span>
          <Button size="sm" onClick={markUploaded} className={`${ACT(!uploaded, uploaded)} gap-1 h-8 text-xs`} data-testid={`uploaded-${post.id}`}>
            <CheckCircle2 className="w-3.5 h-3.5" /> <span>{uploaded ? t("تم الرفع ✓", "Uploaded ✓") : t("ارفع وأكّد", "Uploaded — confirm")}</span>
          </Button>
        </StepRow>
      )}

      {/* 5) الأداء */}
      {!auto && (uploaded || needMetrics) && (
        <StepRow n="5" label={t("أرقام الأداء", "Performance")} state={needMetrics ? "need" : metricsSaved ? "done" : "idle"} testId={`metrics-row-${post.id}`}>
          <MetricInput icon={Heart} label={t("إعجابات", "Likes")} color="text-rose-400" value={metrics.likes} onChange={(v) => setM("likes", v)} testid={`likes-${post.id}`} />
          <MetricInput icon={MessageCircle} label={t("تعليقات", "Comments")} color="text-sky-400" value={metrics.comments} onChange={(v) => setM("comments", v)} testid={`comments-${post.id}`} />
          <MetricInput icon={Repeat2} label={t("إعادة نشر", "Reposts")} color="text-accent" value={metrics.reposts} onChange={(v) => setM("reposts", v)} testid={`reposts-${post.id}`} />
          <Button size="sm" onClick={saveMetrics} disabled={savingM} className={`${ACT(!metricsSaved, metricsSaved)} gap-1 h-9 text-xs self-end`} data-testid={`save-metrics-${post.id}`}>
            {savingM ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : metricsSaved ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Save className="w-3.5 h-3.5" />}
            <span>{metricsSaved ? t("تم حفظ الأداء ✓", "Performance saved ✓") : t("احفظ الأداء", "Save performance")}</span>
          </Button>
        </StepRow>
      )}

      {isTikTok && <TikTokPostOptions open={ttOpen} onOpenChange={setTtOpen} campaignId={campaignId} post={post} projectId={projectId} onSaved={(o) => { setTtOpts(o); onPatch(campaignId, post.id, { tiktok: o }); }} />}

      {/* 6) التقييم بعد اكتمال المنشور */}
      {complete && (
        <ResultRating product="campaign_post" contentId={post.content_id} projectId={campaignId} className="mt-1" />
      )}
    </div>
  );
}

// لون الإجراء: أحمر = مطلوب قرار/إدخال من العميل، أخضر = اعتُمد/اكتمل، بنفسجي = متاح
const ACT = (need, done = false) => done ? "bg-emerald-600 hover:bg-emerald-700 text-white" : need ? "bg-rose-600 hover:bg-rose-700 text-white" : "bg-primary hover:bg-primary/90 text-white";

function StepRow({ n, label, state, children, testId }) {
  const ring = state === "need" ? "border-rose-500 ring-2 ring-rose-500/70 bg-rose-500/5" : state === "done" ? "border-emerald-500/40 bg-emerald-500/5" : "border-border bg-background/40";
  const badge = state === "need" ? "bg-rose-600 text-white" : state === "done" ? "bg-emerald-600 text-white" : "bg-primary/15 text-primary";
  return (
    <div className={`mt-3 rounded-xl border px-3 py-2.5 ${ring}`} data-testid={testId}>
      <div className="flex items-center gap-2 mb-2">
        <span className={`w-5 h-5 rounded-full text-[10px] font-extrabold flex items-center justify-center shrink-0 ${badge}`}>{state === "done" ? "✓" : n}</span>
        <span className={`text-[11px] font-bold ${state === "need" ? "text-rose-400" : state === "done" ? "text-emerald-400" : "text-muted-foreground"}`}>{label}{state === "need" ? ` — ${localStorage.getItem("rawaj_lang") === "en" ? "action required" : "مطلوب منك"}` : ""}</span>
      </div>
      <div className="flex flex-wrap items-center gap-2">{children}</div>
    </div>
  );
}

function MetricInput({ icon: Icon, label, color, value, onChange, testid }) {
  return (
    <div className="w-[96px]">
      <div className={`text-[10px] mb-1 flex items-center gap-1 ${color}`}><Icon className="w-3 h-3" /> {label}</div>
      <Input type="number" value={value} onChange={(e) => onChange(e.target.value)}
        dir="ltr" className="bg-background border-border h-9 text-center text-sm" data-testid={`metric-input-${testid}`} />
    </div>
  );
}
